import { useAnalytics } from '@/components/molecules/AnalyticsProvider';
import { Dialog } from '@/components/molecules/Dialog';
import { GOOGLE_TRACK_INFO } from '@/lib/constants';
import { formatPhoneNumberWithInputMask } from '@/lib/phoneFormatter';
import { VehicleListingType, VehicleType } from '@/lib/schema/inventory/types';
import { generateVehicleDetailQuery } from '@/lib/vehicle';
import { PhoneIcon } from '@heroicons/react/24/solid';
import Image from 'next/image';
import { useEffect, useState } from 'react';

interface CallModalProps {
  open: boolean;
  onClose: () => void;
  product: VehicleType | VehicleListingType;
}

// Map US state codes to IANA timezone identifiers
const STATE_TIMEZONE_MAP: Record<string, string> = {
  AL: 'America/Chicago',
  AK: 'America/Anchorage',
  AZ: 'America/Phoenix',
  AR: 'America/Chicago',
  CA: 'America/Los_Angeles',
  CO: 'America/Denver',
  CT: 'America/New_York',
  DE: 'America/New_York',
  FL: 'America/New_York',
  GA: 'America/New_York',
  HI: 'Pacific/Honolulu',
  ID: 'America/Denver',
  IL: 'America/Chicago',
  IN: 'America/New_York',
  IA: 'America/Chicago',
  KS: 'America/Chicago',
  KY: 'America/New_York',
  LA: 'America/Chicago',
  ME: 'America/New_York',
  MD: 'America/New_York',
  MA: 'America/New_York',
  MI: 'America/New_York',
  MN: 'America/Chicago',
  MS: 'America/Chicago',
  MO: 'America/Chicago',
  MT: 'America/Denver',
  NE: 'America/Chicago',
  NV: 'America/Los_Angeles',
  NH: 'America/New_York',
  NJ: 'America/New_York',
  NM: 'America/Denver',
  NY: 'America/New_York',
  NC: 'America/New_York',
  ND: 'America/Chicago',
  OH: 'America/New_York',
  OK: 'America/Chicago',
  OR: 'America/Los_Angeles',
  PA: 'America/New_York',
  RI: 'America/New_York',
  SC: 'America/New_York',
  SD: 'America/Chicago',
  TN: 'America/Chicago',
  TX: 'America/Chicago',
  UT: 'America/Denver',
  VT: 'America/New_York',
  VA: 'America/New_York',
  WA: 'America/Los_Angeles',
  WV: 'America/New_York',
  WI: 'America/Chicago',
  WY: 'America/Denver',
};

// Parse time string (e.g., "09:00" or "17:30") to hours and minutes
const parseTime = (timeStr: string): { hours: number; minutes: number } => {
  const [hours, minutes] = timeStr.split(':').map(Number);
  return { hours, minutes };
};

// Function to check if current time is during business hours (timezone-aware)
// Default hours: Monday-Friday, 8 AM - 6 PM in dealer's local timezone
const isBusinessHours = (
  currentDate: Date,
  dealerState?: string,
  dealerHours?: { startTime: string; endTime: string } | null
): boolean => {
  // Get dealer's timezone based on their state
  const timezone = dealerState
    ? STATE_TIMEZONE_MAP[dealerState.toUpperCase()] || 'America/New_York'
    : 'America/New_York';

  // Convert current time to dealer's local timezone
  const dealerLocalTime = new Date(
    currentDate.toLocaleString('en-US', { timeZone: timezone })
  );

  const hour = dealerLocalTime.getHours();
  const minutes = dealerLocalTime.getMinutes();
  const day = dealerLocalTime.getDay(); // 0 = Sunday, 6 = Saturday

  // Check if it's a weekday (Monday-Friday)
  const isWeekday = day >= 1 && day <= 5;

  if (!isWeekday) {
    return false;
  }

  // Use dealer hours if available, otherwise default to 8 AM - 6 PM
  const startTime = dealerHours?.startTime || '08:00';
  const endTime = dealerHours?.endTime || '18:00';

  const start = parseTime(startTime);
  const end = parseTime(endTime);

  const currentTimeInMinutes = hour * 60 + minutes;
  const startTimeInMinutes = start.hours * 60 + start.minutes;
  const endTimeInMinutes = end.hours * 60 + end.minutes;

  return (
    currentTimeInMinutes >= startTimeInMinutes &&
    currentTimeInMinutes < endTimeInMinutes
  );
};

export const CallModal: React.FC<CallModalProps> = ({
  open,
  onClose,
  product,
}) => {
  const { dealer, make, model, year, shortListingId, listingId } = product;
  const { handleActionTracking } = useAnalytics();
  const vehicleDetailQuery = generateVehicleDetailQuery(
    make,
    model,
    year,
    shortListingId || listingId
  );

  // CarAI phone number from environment variable
  const carAIPhoneNumber =
    process.env.NEXT_PUBLIC_CARAI_PHONE_NUMBER || '+12365069678';
  const carAIPhoneNumberFormatted =
    formatPhoneNumberWithInputMask(carAIPhoneNumber);

  const isDev = process.env.NEXT_PUBLIC_ENABLE_TIME_SIMULATOR === 'true';
  const [simulatedTime, setSimulatedTime] = useState<string>('');
  const [currentDate, setCurrentDate] = useState<Date>(new Date());
  const [listingExtension, setListingExtension] = useState<string | null>(null);
  const [extensionLoading, setExtensionLoading] = useState<boolean>(true);

  // Fetch listing extension
  useEffect(() => {
    const fetchExtension = async () => {
      try {
        setExtensionLoading(true);
        const idToUse = listingId;

        const response = await fetch(
          `/api/vehicle_details/listing_extension/${idToUse}`
        );

        if (response.ok) {
          try {
            const extension = await response.json();

            // Extension can be a string or number
            setListingExtension(String(extension));
          } catch (jsonError) {
            setListingExtension(null);
          }
        } else {
          setListingExtension(null);
        }
      } catch (error) {
        setListingExtension(null);
      } finally {
        setExtensionLoading(false);
      }
    };

    if (listingId || shortListingId) {
      fetchExtension();
    } else {
      setExtensionLoading(false);
    }
  }, [listingId, shortListingId, isDev]);

  useEffect(() => {
    if (isDev && simulatedTime) {
      // Parse simulated time (format: "HH:MM")
      const [hours, minutes] = simulatedTime.split(':').map(Number);
      const simDate = new Date();
      simDate.setHours(hours, minutes, 0, 0);
      setCurrentDate(simDate);
      return undefined;
    }
    // Update current time every second when not simulating
    const updateTime = () => setCurrentDate(new Date());
    updateTime(); // Initial update
    const interval = setInterval(updateTime, 1000);
    return () => clearInterval(interval);
  }, [isDev, simulatedTime]);

  // TODO: Fetch dealer hours from API when dealer hours are available
  // For now, using default hours (8 AM - 6 PM in dealer's local timezone)
  const dealerHours = null; // Will be: dealer?.hoursOfOperation?.[currentWeekday]

  // Show AI design only during off-hours when extension is successfully fetched
  // Note: dealer.province contains the state code from listing.State
  const showAIDesign =
    !extensionLoading &&
    !!listingExtension &&
    !isBusinessHours(currentDate, dealer?.province, dealerHours);

  // Original simple design for business hours
  if (!showAIDesign) {
    return (
      <Dialog open={open} onOpenChange={onClose}>
        <div className="flex flex-col gap-l p-xl">
          {/* Dev-only time simulator */}
          {isDev && (
            <div className="mb-4 rounded border border-yellow-400 bg-yellow-50 p-3">
              <label className="mb-2 block text-xs font-semibold text-yellow-900">
                🛠️ Dev Time Simulator (HH:MM)
              </label>
              <input
                type="time"
                value={simulatedTime}
                onChange={(e) => setSimulatedTime(e.target.value)}
                className="w-full rounded border border-yellow-400 px-2 py-1 text-sm"
                placeholder="HH:MM"
              />
              <div className="mt-1 text-xs text-yellow-700">
                Your Time: {currentDate.toLocaleTimeString()} • Dealer Time:{' '}
                {dealer?.province
                  ? currentDate.toLocaleString('en-US', {
                      timeZone:
                        STATE_TIMEZONE_MAP[dealer.province.toUpperCase()] ||
                        'America/New_York',
                      hour: '2-digit',
                      minute: '2-digit',
                      second: '2-digit',
                    })
                  : 'N/A'}{' '}
                ({dealer?.province || 'N/A'}) • Hours: 8 AM - 6 PM •{' '}
                {isBusinessHours(currentDate, dealer?.province, dealerHours)
                  ? 'Business Hours'
                  : 'Off-Hours'}{' '}
                • Extension: {listingExtension || 'None'} •{' '}
                {showAIDesign ? 'AI Design' : 'Regular Design'}
              </div>
            </div>
          )}

          {dealer && dealer.name && dealer.city && dealer.province ? (
            <div className="text-body2Medium font-bold">
              {dealer.name}, {dealer.city}, {dealer.province}
            </div>
          ) : (
            <div className="text-body2Medium font-bold">
              No dealer info found
            </div>
          )}
          {dealer?.phone && (
            <div className="flex items-center gap-s">
              <PhoneIcon width={20} height={20} fill="#000000" />
              <a
                href={`tel: ${dealer.phone}`}
                onClick={() => {
                  handleActionTracking({
                    ...GOOGLE_TRACK_INFO.callDealerButton,
                    pageTarget: dealer?.phone
                      ? `/vehicle/${vehicleDetailQuery}?dealerPhone=${dealer.phone}`
                      : `/vehicle/${vehicleDetailQuery}`,
                  });
                }}
              >
                <span className="text-body1Medium text-blue-medium hover:text-blue-dark hover:underline">
                  {formatPhoneNumberWithInputMask(dealer.phone.toString())}
                </span>
              </a>
            </div>
          )}
        </div>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <div className="flex flex-col">
        {/* Header */}
        {dealer && dealer.name && dealer.city && dealer.province ? (
          <div className="border-b border-neutral-200 px-6 py-4">
            <h3 className="text-lg font-semibold text-neutral-900">
              {dealer.name}, {dealer.city}, {dealer.province}
            </h3>
          </div>
        ) : (
          <div className="border-b border-neutral-200 px-6 py-4">
            <h3 className="text-lg font-semibold text-neutral-900">
              No dealer info found
            </h3>
          </div>
        )}

        {/* Body */}
        <div className="flex flex-col gap-6 px-6 py-6">
          {/* Dev-only time simulator */}
          {isDev && (
            <div className="rounded border border-yellow-400 bg-yellow-50 p-3">
              <label className="mb-2 block text-xs font-semibold text-yellow-900">
                🛠️ Dev Time Simulator (HH:MM)
              </label>
              <input
                type="time"
                value={simulatedTime}
                onChange={(e) => setSimulatedTime(e.target.value)}
                className="w-full rounded border border-yellow-400 px-2 py-1 text-sm"
                placeholder="HH:MM"
              />
              <div className="mt-1 text-xs text-yellow-700">
                Your Time: {currentDate.toLocaleTimeString()} • Dealer Time:{' '}
                {dealer?.province
                  ? currentDate.toLocaleString('en-US', {
                      timeZone:
                        STATE_TIMEZONE_MAP[dealer.province.toUpperCase()] ||
                        'America/New_York',
                      hour: '2-digit',
                      minute: '2-digit',
                      second: '2-digit',
                    })
                  : 'N/A'}{' '}
                ({dealer?.province || 'N/A'}) • Hours: 8 AM - 6 PM •{' '}
                {isBusinessHours(currentDate, dealer?.province, dealerHours)
                  ? 'Business Hours'
                  : 'Off-Hours'}{' '}
                • Extension: {listingExtension || 'None'} •{' '}
                {showAIDesign ? 'AI Design' : 'Regular Design'}
              </div>
            </div>
          )}

          {!extensionLoading && listingExtension && (
            <p className="text-sm text-neutral-700">
              Call the number. When prompted, enter the vehicle&apos;s extension
              number and then press the pound key (#).
            </p>
          )}

          {/* Contact Information */}
          <div className="flex flex-row flex-wrap items-center gap-x-6 gap-y-4">
            {/* Phone Number - Show CarAI number when extension is available */}
            <div className="flex items-center gap-3">
              <PhoneIcon className="h-6 w-6 flex-shrink-0 text-neutral-900" />
              <a
                href={`tel:${carAIPhoneNumber}`}
                onClick={() => {
                  handleActionTracking({
                    ...GOOGLE_TRACK_INFO.callDealerButton,
                    pageTarget: dealer?.phone
                      ? `/vehicle/${vehicleDetailQuery}?dealerPhone=${dealer.phone}`
                      : `/vehicle/${vehicleDetailQuery}`,
                  });
                }}
                className="text-xl font-medium text-blue-600 hover:text-blue-700 hover:underline"
              >
                +1 {carAIPhoneNumberFormatted}
              </a>
            </div>

            {/* Extension Number */}
            {!extensionLoading && listingExtension && (
              <div className="flex items-center gap-3">
                <Image
                  src="/images/caraibanner/car.svg"
                  alt="Car"
                  width={24}
                  height={24}
                  className="h-6 w-6 flex-shrink-0 text-neutral-900"
                />
                <span className="text-xl font-medium text-neutral-900">
                  {listingExtension}#
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-center gap-2 border-t border-neutral-200 bg-neutral-50 px-6 py-3">
          <span className="text-xs text-neutral-600">
            24/7 Voice AI Powered By
          </span>
          <Image
            src="/images/caraibanner/logo2.svg"
            alt="CarAi"
            width={50}
            height={20}
            className="h-5 w-auto"
          />
        </div>
      </div>
    </Dialog>
  );
};
